import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { ConfigureNavIcon } from './configure-nav-icon';
import { getVisibleConfigureNavItems } from 'constant/configure-nav';
import { isPathActive } from 'utils/is-path-active';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useSidebar } from 'components/ui/sidebar';
import {
  SIDEBAR_NAV_ICON_SIZE,
  SIDEBAR_NAV_ICON_STROKE,
  sidebarNavIconClass,
} from './sidebar-nav-icon';
import { sidebarNavItemLabelClass } from './sidebar-nav-label';
import { sidebarNavItemIconOnlyClassName } from './sidebar-nav-item-styles';
import { SidebarNavTooltip } from './sidebar-nav-tooltip';

interface SidebarConfigurePanelProps {
  onBack?: () => void;
}

export function SidebarConfigurePanel({ onBack }: SidebarConfigurePanelProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const userRoles = useCommonStore((state) => state.user?.roles ?? []);
  const { isMobile,isSidebarOverlay, isConfigureInline, setOpenMobile, open } = useSidebar();
  const iconOnly = isConfigureInline && !open;
  const navItems = getVisibleConfigureNavItems(userRoles);

  return (
    <aside
      className={cn(
        'flex shrink-0 flex-col',
        isConfigureInline
          ? iconOnly
            ? 'w-full gap-0 bg-transparent px-0 py-0'
            : 'w-full gap-0 bg-transparent px-3 py-0'
          : 'h-full w-[var(--sidebar-configure-width)] gap-6 border-r border-[#E7E7E7] bg-[#F6F6F6] px-3 py-6 dark:border-sidebar-border dark:bg-sidebar'
      )}
      aria-label={t('ADVANCED') || 'Advanced'}
    >
      {!isConfigureInline && onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex h-[38px] items-center gap-2 px-2 text-[#3D3D3D] transition-opacity hover:opacity-80 dark:text-sidebar-foreground"
        >
          <ArrowLeft width={20} height={20} strokeWidth={1.5} />
          <span className="text-base font-medium leading-tight">{t('ADVANCED') || 'Advanced'}</span>
        </button>
      )}

      <nav className="flex flex-col">
        {navItems.map((item) => {
          const active = isPathActive(pathname, item.path);
          const label = t(item.labelKey);

          return (
            <SidebarNavTooltip key={item.id} label={label} enabled={iconOnly}>
              <Link
                to={item.path}
                onClick={() => {
                  if (isSidebarOverlay) {
                    setOpenMobile(false);
                  }
                }}
                aria-current={active ? 'page' : undefined}
                aria-label={iconOnly ? label : undefined}
                className={cn(
                  'flex h-[38px] w-full items-center rounded-md transition-colors hover:bg-[#E7E7E7] dark:hover:bg-[var(--sidebar-pill-dark)]',
                  iconOnly ? sidebarNavItemIconOnlyClassName : 'gap-3 px-2 pr-[18px]',
                  active && 'bg-[#E7E7E7] dark:bg-[var(--sidebar-pill-dark)] dark:text-grey-50'
                )}
              >
                <ConfigureNavIcon
                  icon={item.icon}
                  size={SIDEBAR_NAV_ICON_SIZE}
                  strokeWidth={SIDEBAR_NAV_ICON_STROKE}
                  className={cn(sidebarNavIconClass, active && 'dark:text-true-white')}
                />
                {!iconOnly && (
                  <span
                    className={cn(
                      sidebarNavItemLabelClass,
                      'truncate text-sm',
                      active && 'dark:text-grey-50'
                    )}
                  >
                    {label}
                  </span>
                )}
              </Link>
            </SidebarNavTooltip>
          );
        })}
      </nav>
    </aside>
  );
}